<template>
  <div class="document-container">
    <es-loader
      v-if="loading"
      style="margin-top: 15rem"
    />
    <es-exception
      v-else-if="loadingError"
      margin="15rem"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
    />
    <div v-else>
      <pid-retired-id-panel
        v-if="isRetired"
        :person="person"
      />
      <template
        v-if="$route.name === 'PidAssociatedRecords'
          || $route.name === 'DmAssociatedRecords'"
      >
        <div
          class="document"
          :class="{ 'margin--top': isRetired }"
        >
          <div class="header">
            <es-header>
              <template #header>
                <div class="header-title">
                  Associated Records
                </div>
              </template>
            </es-header>
          </div>
          <div class="content">
            <template
              v-if="$route.name === 'PidAssociatedRecords'
                || $route.name === 'DmAssociatedRecords'"
            >
              <es-exception
                v-if="this.totalRecords.length === 0"
                margin="5rem"
                exceptionMessage="No Associated records."
              />
              <table v-else class="table is-hoverable is-fullwidth">
                <es-sorter
                  :columnHeaders="columnHeaders"
                  @updateSorter="updateSorter"
                />
                <person-profile-associated-records-row
                  v-for="(record, key) in totalRecords"
                  :key="key"
                  :record="record"
                  :isRetired="isRetired"
                  :isRecordRetired="isRecordRetired"
                  @retiredReason="emitReason($event)"
                />
                <tfoot v-if="!loading && totalRecords && totalRecords.length">
                  <tr>
                    <td colspan="100%">
                      <es-pager :totalCount="totalCount"/>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </template>
          </div>
        </div>
      </template>
      <template v-else>
        <router-view name="personProfileSubBody"
          :person="person"
          :isRetired="isRetired"
          :isRecordRetired="isRecordRetired"
        />
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import qs from 'qs';
import { mapGetters } from 'vuex';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsSorter from '@/components/EsSorter.vue';
import PersonProfileAssociatedRecordsRow from '@/components/PersonProfileAssociatedRecordsRow.vue';
import PidRetiredIdPanel from '@/components/PidRetiredIdPanel.vue';
import FieldLabels from '@/mixins/FieldLabels';

export default {
  mixins: [
    FieldLabels,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
    EsPager,
    EsSorter,
    PersonProfileAssociatedRecordsRow,
    PidRetiredIdPanel,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    isRetired: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      apiSorter: {},
      columnHeaders: [],
      loading: true,
      loadingError: null,
      totalRecords: [],
      totalCount: null,
    };
  },
  created() {
    this.getTotalRecords();
    const commonStyle = { width: '10%' };
    const generateHeader = (header, style = commonStyle) => ({
      header,
      title: header,
      style,
    });
    this.columnHeaders = [
      generateHeader('Trans Date'),
      generateHeader('Name'),
      generateHeader(this.fieldLabel('DATE_OF_BIRTH')),
      generateHeader(this.fieldLabel('GENDER_CODE')),
      generateHeader('Location Info', { width: '30%' }),
      generateHeader('Associated Info', { width: '30%' }),
      {
        header: 'Actions',
        title: 'Actions',
      },
    ];
  },
  computed: {
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
    ]),
    isRecordRetired() {
      const { recordRetired } = this.$route.query;
      return recordRetired === 'true' || recordRetired === true;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    sortParams() {
      const params = {};
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      return params;
    },
  },
  methods: {
    emitReason(r) {
      this.$emit('emitFurther', r);
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.person.personType);
    },
    async getTotalRecords() {
      try {
        this.loading = true;
        this.loadingError = false;

        // Fetch associated records
        let associatedRecords = [];
        let associatedRecordsCount = 0;
        const associatedRecordsResponse = await axios.get(`person/${this.id}/associated`, {
          params: this.sortParams,
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
        associatedRecords = associatedRecordsResponse.data.data;
        associatedRecordsCount = associatedRecordsResponse.data.totalCount;

        if (associatedRecords && associatedRecords.length
              && associatedRecords[0].retiredReference
              && associatedRecords[0].retiredReference.idMerged) {
          const profileResponse = await axios.get(`person/${associatedRecords[0].retiredReference.idMerged}/associated`);
          if (profileResponse.status === 204) {
            const temp = await axios.get(`person/${associatedRecords[0].retiredReference.idMerged}/profile`);
            associatedRecords[0] = temp.data;
          } else {
            associatedRecords = profileResponse.data.data;
          }
        }

        // Fetch merged records
        let mergedRecords = [];
        let mergedRecordsCount = 0;
        const mergedRecordsResponse = await axios.get(`person/${this.id}/merged`, {
          params: this.sortParams,
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
        mergedRecords = mergedRecordsResponse.data.data;
        mergedRecordsCount = mergedRecordsResponse.data.totalCount;

        this.totalRecords = (associatedRecords || []).concat(mergedRecords || []).filter(item => item !== '');
        this.totalCount = (associatedRecordsCount || 0) + (mergedRecordsCount || 0);
      } catch (e) {
        console.error('Error fetching merged records:', e);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    updateSorter() {
      this.$router.replace({ query: this.sortParams });
    },
  },
};
</script>
<style lang="sass" scoped>
  .content
    overflow-x: auto
</style>
